
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

type Language = 'fr' | 'en';

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const LanguageContext = createContext<LanguageContextType>({
  language: 'fr',
  setLanguage: () => {},
  t: (key) => key,
});

export const useLanguage = () => useContext(LanguageContext);

const translations: Record<Language, Record<string, string>> = {
  fr: {
    // App
    'app.name': 'Plateforme Citoyenne',
    'app.support': 'Soutenez notre application',

    // Navigation
    'home': 'Accueil',
    'dashboard': 'Tableau de bord',
    'favorites': 'Favoris',
    'collaborative-space': 'Espace Collaboratif',
    'prestataire-space': 'Espace Prestataire',
    'contact-collectivites': 'Contact Collectivités',
    'api-docs': 'API Documentation',
    'impact': 'Impact',
    'login': 'Connexion',
    'more': 'Plus',

    // Theme
    'theme.light': 'Passer en mode clair',
    'theme.dark': 'Passer en mode sombre',

    // Hero (Landing)
    'hero.title': 'Améliorons notre ville ensemble',
    'hero.subtitle': 'Participez activement à l\'amélioration de votre cadre de vie en signalant les problèmes urbains à votre mairie.',
    'hero.cta.primary': 'Commencer maintenant',
    'hero.cta.secondary': 'En savoir plus',

    // Accessibility
    'accessibility.skipLink': 'Aller au contenu principal',

    // Reports
    'reports.new': 'Nouveau signalement',
    'reports.view': 'Voir les signalements',

    // Landing Features
    'features.title': 'Pourquoi nous rejoindre ?',
    'features.community.title': 'Communauté engagée',
    'features.community.desc': 'Rejoignez une communauté de citoyens engagés pour l\'amélioration de leur ville.',
    'features.realtime.title': 'Suivi en temps réel',
    'features.realtime.desc': 'Suivez l\'évolution de vos signalements et restez informé des actions entreprises.',
    'features.security.title': 'Sécurité garantie',
    'features.security.desc': 'Vos données sont protégées et traitées en toute confidentialité.',

    // How It Works
    'howItWorks.title': 'Comment ça marche ?',
    'howItWorks.subtitle': 'Notre plateforme simplifie l\'engagement citoyen en quelques étapes simples. Découvrez comment participer activement à l\'amélioration de votre ville.',
    'howItWorks.step': 'Étape',
    'howItWorks.step1.title': 'Signalez un problème',
    'howItWorks.step1.desc': 'Identifiez un problème dans votre quartier et signalez-le en quelques clics avec photo et localisation.',
    'howItWorks.step2.title': 'Localisez précisément',
    'howItWorks.step2.desc': 'Positionnez le signalement sur la carte pour une intervention efficace des services concernés.',
    'howItWorks.step3.title': 'Soutenez les initiatives',
    'howItWorks.step3.desc': 'Votez pour les signalements qui vous paraissent prioritaires et augmentez leur visibilité.',
    'howItWorks.step4.title': 'Suivez les résolutions',
    'howItWorks.step4.desc': 'Recevez des notifications sur l\'avancement des travaux et la résolution des problèmes.',
    'howItWorks.step5.title': 'Contribuez collectivement',
    'howItWorks.step5.desc': 'Participez aux discussions et proposez vos solutions pour améliorer votre cadre de vie.',
    'howItWorks.step6.title': 'Constatez l\'impact',
    'howItWorks.step6.desc': 'Visualisez l\'impact concret de votre participation grâce aux statistiques et aux témoignages.',

    // Call to Action
    'cta.title': 'Prêt à améliorer votre ville ?',
    'cta.subtitle': 'Inscrivez-vous gratuitement et agissez pour être utile maintenant.',
    'cta.button': 'Créer un compte gratuitement',

    // Footer
    'footer.about': 'À propos',
    'footer.about.desc': 'Là où tout commence est une plateforme innovante dédiée à l\'amélioration de notre environnement urbain grâce à la participation citoyenne.',
    'footer.contact': 'Contact',
    'footer.quickLinks': 'Liens rapides',
    'footer.howItWorks': 'Comment ça marche',
    'footer.commitments': 'Nos engagements',
    'footer.legal': 'Mentions légales',
    'footer.privacy': 'Confidentialité',
    'footer.privacyPolicy': 'Politique de confidentialité',
    'footer.cookies': 'Cookies',
    'footer.terms': 'CGU',
    'footer.followUs': 'Suivez-nous',
    'footer.copyright': '© 2024 Là où tout commence. Tous droits réservés.',

    // Launch Hero
    'launch.badge': 'Lancement imminent',
    'launch.earlySubscribers': '{{count}}+ premiers inscrits',
    'launch.limitedSpots': 'Places limitées',
    'launch.hero.title': 'Signalez. Suivez.',
    'launch.hero.titleAccent': 'Transformez votre ville.',
    'launch.hero.subtitle': 'Nids-de-poule, éclairage cassé, déchets sauvages — prenez une photo, votre mairie agit.',
    'launch.hero.joinCitizens': 'Rejoignez {{count}} citoyens déjà engagés.',
    'launch.hero.joinFirst': 'Rejoignez les citoyens déjà engagés.',
    'launch.hero.guarantee': '✓ Gratuit pour toujours · ✓ Zéro spam · ✓ Accès prioritaire',

    // Launch Features
    'launch.features.title': 'Tout ce qu\'il faut pour transformer votre ville',
    'launch.features.subtitle': 'Une plateforme complète connectant citoyens et collectivités locales',
    'launch.features.geolocated': 'Signalements géolocalisés',
    'launch.features.geolocated.desc': 'Photo + GPS pour des interventions précises dans votre quartier',
    'launch.features.realtime': 'Suivi en temps réel',
    'launch.features.realtime.desc': 'Soyez notifié à chaque étape du traitement de votre signalement',
    'launch.features.voting': 'Vote communautaire',
    'launch.features.voting.desc': 'Les citoyens votent pour prioriser les problèmes les plus urgents',
    'launch.features.impact': 'Impact mesurable',
    'launch.features.impact.desc': 'Tableaux de bord montrant les changements concrets et 76% de résolution',
    'launch.features.categories': '20+ catégories',
    'launch.features.categories.desc': 'Voirie, propreté, sécurité, environnement, eau, éclairage…',
    'launch.features.gamification': 'Gamification',
    'launch.features.gamification.desc': 'Gagnez des badges et grimpez dans le classement citoyen',

    // Launch How It Works
    'launch.howItWorks.title': 'Comment ça marche',
    'launch.howItWorks.subtitle': 'Trois étapes pour améliorer votre ville',
    'launch.howItWorks.step': 'Étape',
    'launch.howItWorks.step1': 'Signalez',
    'launch.howItWorks.step1.desc': 'Prenez une photo et décrivez le problème — ça prend 30 secondes',
    'launch.howItWorks.step2': 'Suivez',
    'launch.howItWorks.step2.desc': 'Suivez votre signalement en temps réel et mobilisez la communauté',
    'launch.howItWorks.step3': 'Résolvez',
    'launch.howItWorks.step3.desc': 'Les autorités locales agissent et vous voyez l\'impact sur votre ville',

    // Launch Stats
    'launch.stats.categories': 'Catégories de signalement',
    'launch.stats.resolution': 'Taux de résolution',
    'launch.stats.time': 'Pour créer un signalement',
    'launch.stats.roi': 'ROI social',

    // Launch Social Proof
    'launch.social.title': 'Ce que disent nos premiers testeurs',
    'launch.social.quote1': 'Enfin une appli qui redonne le pouvoir aux citoyens !',
    'launch.social.author1': 'Marie L.',
    'launch.social.role1': 'Habitante, Lyon',
    'launch.social.quote2': 'L\'interface est intuitive — signaler prend 30 secondes.',
    'launch.social.author2': 'Thomas R.',
    'launch.social.role2': 'Bêta-testeur',
    'launch.social.quote3': 'Un outil indispensable pour les collectivités locales.',
    'launch.social.author3': 'Sophie M.',
    'launch.social.role3': 'Élue municipale',

    // Launch Final CTA
    'launch.finalCta.title': 'Prêt à améliorer votre ville ?',
    'launch.finalCta.subtitle': 'Rejoignez les premiers citoyens engagés et obtenez un accès prioritaire.',
    'launch.finalCta.subscriberCount': '{{count}}+ personnes déjà inscrites',
    'launch.finalCta.beFirst': 'Soyez parmi les premiers',
    'launch.finalCta.launchSoon': 'Lancement imminent',

    // Welcome (Product Hunt + BetaList)
    'welcome.badge': 'Merci Product Hunt ! 🎉',
    'welcome.badge.betalist': 'Découvert sur BetaList 🚀',
    'welcome.title': 'Votre ville mérite',
    'welcome.titleAccent': 'mieux.',
    'welcome.subtitle': 'Nids-de-poule, éclairage cassé, déchets sauvages — signalez en 30 secondes, votre mairie agit. Rejoignez le mouvement citoyen.',
    'welcome.subtitle.betalist': 'Vous faites partie des premiers à découvrir KLIKAN. Signalez les problèmes urbains en 30 secondes et transformez votre ville. Accès exclusif early adopter.',
    'welcome.highlight1.title': '30s pour signaler',
    'welcome.highlight1.desc': 'Photo + géolocalisation automatique',
    'welcome.highlight2.title': '76% de résolution',
    'welcome.highlight2.desc': 'Suivi transparent en temps réel',
    'welcome.highlight3.title': '2,847 citoyens actifs',
    'welcome.highlight3.desc': 'Communauté engagée et grandissante',
    'welcome.earlyAccess': '🚀 Accès prioritaire — offre Product Hunt',
    'welcome.earlyAccess.betalist': '🚀 Accès exclusif — Offre BetaList',
    'welcome.guarantee': '✓ Gratuit pour toujours · ✓ Zéro spam · ✓ Accès prioritaire',
    'welcome.guarantee.betalist': '✓ Gratuit pour toujours · ✓ Zéro spam · ✓ Badge early adopter',
    'welcome.betalist.featured': 'Sélectionné et mis en avant sur BetaList',
    'welcome.betalist.spotsLeft': 'Plus que {{count}} places early adopter',
    'welcome.betalist.launchIn': 'Lancement dans',
    'welcome.betalist.days': 'j',
    'welcome.betalist.hours': 'h',
    'welcome.betalist.minutes': 'min',
    'welcome.betalist.seconds': 's',
    'welcome.explore': 'Explorer la plateforme',
    'welcome.seeImpact': 'Voir notre impact',
    'welcome.howItWorks': 'Comment ça marche ?',
    'welcome.step1': 'Photographiez',
    'welcome.step1.desc': 'Prenez le problème en photo',
    'welcome.step2': 'Signalez',
    'welcome.step2.desc': 'Géolocalisation automatique + catégorie',
    'welcome.step3': 'Suivez',
    'welcome.step3.desc': 'Notification quand c\'est résolu',

    // Welcome Social Proof
    'welcome.social.title': 'Ils nous font déjà confiance',
    'welcome.social.subscriberCount': '{{count}} citoyens inscrits en accès anticipé',
    'welcome.social.quote1': 'Enfin une plateforme qui donne une vraie voix aux citoyens. J\'ai signalé un nid-de-poule et il a été réparé en 5 jours !',
    'welcome.social.author1': 'Marie D.',
    'welcome.social.role1': 'Citoyenne engagée, Lyon',
    'welcome.social.quote2': 'Un outil indispensable pour notre commune. La transparence avec les habitants a complètement changé.',
    'welcome.social.author2': 'Thomas R.',
    'welcome.social.role2': 'Élu municipal, Bordeaux',
    'welcome.social.quote3': 'Simple, efficace et gratuit. Notre association l\'utilise au quotidien pour coordonner nos actions.',
    'welcome.social.author3': 'Sophie L.',
    'welcome.social.role3': 'Présidente d\'association, Nantes',

    // Categories
    'cat.voirie': 'Voirie',
    'cat.voirie.desc': 'Problèmes liés aux routes, trottoirs et infrastructures routières',
    'cat.proprete': 'Propreté',
    'cat.proprete.desc': 'Dépôts sauvages, poubelles pleines, graffitis',
    'cat.eclairage': 'Éclairage',
    'cat.eclairage.desc': 'Lampadaires défectueux ou zones mal éclairées',
    'cat.transport': 'Transport',
    'cat.transport.desc': 'Problèmes liés aux transports en commun ou à la mobilité',
    'cat.espaces-verts': 'Espaces verts',
    'cat.espaces-verts.desc': 'Entretien des parcs, jardins et plantations',
    'cat.eau': 'Eau',
    'cat.eau.desc': 'Fuites, inondations, problèmes d\'écoulement',
    'cat.securite': 'Sécurité',
    'cat.securite.desc': 'Zones dangereuses ou problèmes nécessitant une intervention des autorités',
    'cat.pollution-air': 'Pollution de l\'air',
    'cat.pollution-air.desc': 'Problèmes liés à la qualité de l\'air et aux émissions polluantes',
    'cat.pollution-eau': 'Pollution de l\'eau',
    'cat.pollution-eau.desc': 'Contamination des cours d\'eau, étangs ou autres points d\'eau',
    'cat.nuisance-sonore': 'Nuisance sonore',
    'cat.nuisance-sonore.desc': 'Bruits excessifs, problèmes acoustiques dans l\'espace public',
    'cat.biodiversite': 'Biodiversité',
    'cat.biodiversite.desc': 'Questions relatives à la protection des espèces et écosystèmes',
    'cat.flore-invasive': 'Flore invasive',
    'cat.flore-invasive.desc': 'Présence de plantes invasives nécessitant un traitement',
    'cat.inondation': 'Inondation',
    'cat.inondation.desc': 'Zones inondées ou à risque d\'inondation',
    'cat.ilot-chaleur': 'Îlot de chaleur',
    'cat.ilot-chaleur.desc': 'Zones urbaines particulièrement chaudes nécessitant des aménagements',
    'cat.erosion': 'Érosion',
    'cat.erosion.desc': 'Problèmes d\'érosion des sols ou des berges',
    'cat.ventilation': 'Ventilation',
    'cat.ventilation.desc': 'Problèmes de circulation d\'air dans les espaces publics',
    'cat.pollution-visuelle': 'Pollution visuelle',
    'cat.pollution-visuelle.desc': 'Nuisances visuelles, affichages sauvages, dégradations esthétiques',
    'cat.commerce-alimentaire': 'Commerce alimentaire',
    'cat.commerce-alimentaire.desc': 'Problèmes liés aux commerces alimentaires',
    'cat.commerce': 'Commerce',
    'cat.commerce.desc': 'Problèmes liés aux commerces et services de proximité',
    'cat.urgence': 'Urgence',
    'cat.urgence.desc': 'Situations nécessitant une intervention rapide',
    'cat.terrain-abandonne': 'Terrain abandonné',
    'cat.terrain-abandonne.desc': 'Terrains en friche ou abandonnés posant des problèmes',

    // Mobile bottom nav
    'nav.home': 'Accueil',
    'nav.stats': 'Stats',
    'nav.report': 'Signaler',
    'nav.favorites': 'Favoris',
    'nav.profile': 'Profil',

    // Toasts
    'toast.alreadySubscribed': 'Vous êtes déjà inscrit ! 🎉',
    'toast.welcomeEarly': 'Bienvenue ! Vous aurez un accès prioritaire 🎉',
    'toast.welcomePH': 'Bienvenue ! Accès prioritaire garanti 🎉',
    'toast.welcomeBL': 'Bienvenue early adopter ! Accès exclusif garanti 🚀',
    'toast.error': 'Une erreur est survenue. Veuillez réessayer.',
  },
  en: {
    // App
    'app.name': 'Citizen Platform',
    'app.support': 'Support our application',

    // Navigation
    'home': 'Home',
    'dashboard': 'Dashboard',
    'favorites': 'Favorites',
    'collaborative-space': 'Collaborative Space',
    'prestataire-space': 'Service Provider Space',
    'contact-collectivites': 'Contact Communities',
    'api-docs': 'API Documentation',
    'impact': 'Impact',
    'login': 'Login',
    'more': 'More',

    // Theme
    'theme.light': 'Switch to light mode',
    'theme.dark': 'Switch to dark mode',

    // Hero (Landing)
    'hero.title': 'Let\'s improve our city together',
    'hero.subtitle': 'Actively participate in improving your living environment by reporting urban issues to your city hall.',
    'hero.cta.primary': 'Get started now',
    'hero.cta.secondary': 'Learn more',

    // Accessibility
    'accessibility.skipLink': 'Skip to main content',

    // Reports
    'reports.new': 'New report',
    'reports.view': 'View reports',

    // Landing Features
    'features.title': 'Why join us?',
    'features.community.title': 'Engaged community',
    'features.community.desc': 'Join a community of engaged citizens working to improve their city.',
    'features.realtime.title': 'Real-time tracking',
    'features.realtime.desc': 'Track your reports and stay informed of actions taken.',
    'features.security.title': 'Guaranteed security',
    'features.security.desc': 'Your data is protected and processed with full confidentiality.',

    // How It Works
    'howItWorks.title': 'How it works',
    'howItWorks.subtitle': 'Our platform simplifies civic engagement in a few simple steps. Discover how to actively participate in improving your city.',
    'howItWorks.step': 'Step',
    'howItWorks.step1.title': 'Report an issue',
    'howItWorks.step1.desc': 'Identify a problem in your neighborhood and report it in a few clicks with a photo and location.',
    'howItWorks.step2.title': 'Pinpoint location',
    'howItWorks.step2.desc': 'Place the report on the map for efficient intervention by the relevant services.',
    'howItWorks.step3.title': 'Support initiatives',
    'howItWorks.step3.desc': 'Vote for the reports you consider a priority and increase their visibility.',
    'howItWorks.step4.title': 'Track resolutions',
    'howItWorks.step4.desc': 'Receive notifications on work progress and problem resolution.',
    'howItWorks.step5.title': 'Contribute collectively',
    'howItWorks.step5.desc': 'Join discussions and propose your solutions to improve your living environment.',
    'howItWorks.step6.title': 'See the impact',
    'howItWorks.step6.desc': 'Visualize the concrete impact of your participation through statistics and testimonials.',

    // Call to Action
    'cta.title': 'Ready to improve your city?',
    'cta.subtitle': 'Sign up for free and start making a difference now.',
    'cta.button': 'Create a free account',

    // Footer
    'footer.about': 'About',
    'footer.about.desc': 'An innovative platform dedicated to improving our urban environment through citizen participation.',
    'footer.contact': 'Contact',
    'footer.quickLinks': 'Quick links',
    'footer.howItWorks': 'How it works',
    'footer.commitments': 'Our commitments',
    'footer.legal': 'Legal notice',
    'footer.privacy': 'Privacy',
    'footer.privacyPolicy': 'Privacy policy',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Terms of Service',
    'footer.followUs': 'Follow us',
    'footer.copyright': '© 2024 KLIKAN. All rights reserved.',

    // Launch Hero
    'launch.badge': 'Launching soon',
    'launch.earlySubscribers': '{{count}}+ early subscribers',
    'launch.limitedSpots': 'Limited spots',
    'launch.hero.title': 'Report. Track.',
    'launch.hero.titleAccent': 'Transform your city.',
    'launch.hero.subtitle': 'Potholes, broken lights, illegal dumping — take a photo, your city acts.',
    'launch.hero.joinCitizens': 'Join {{count}} citizens already making a difference.',
    'launch.hero.joinFirst': 'Join the citizens already making a difference.',
    'launch.hero.guarantee': '✓ Free forever · ✓ Zero spam · ✓ Priority access',

    // Launch Features
    'launch.features.title': 'Everything you need to transform your city',
    'launch.features.subtitle': 'A complete platform connecting citizens and local authorities',
    'launch.features.geolocated': 'Geolocated reports',
    'launch.features.geolocated.desc': 'Photo + GPS for precise interventions in your neighborhood',
    'launch.features.realtime': 'Real-time tracking',
    'launch.features.realtime.desc': 'Get notified at every step of your report\'s processing',
    'launch.features.voting': 'Community voting',
    'launch.features.voting.desc': 'Citizens vote to prioritize the most urgent issues',
    'launch.features.impact': 'Measurable impact',
    'launch.features.impact.desc': 'Dashboards showing concrete changes and 76% resolution rate',
    'launch.features.categories': '20+ categories',
    'launch.features.categories.desc': 'Roads, cleanliness, safety, environment, water, lighting…',
    'launch.features.gamification': 'Gamification',
    'launch.features.gamification.desc': 'Earn badges and climb the citizen leaderboard',

    // Launch How It Works
    'launch.howItWorks.title': 'How it works',
    'launch.howItWorks.subtitle': 'Three steps to improve your city',
    'launch.howItWorks.step': 'Step',
    'launch.howItWorks.step1': 'Report',
    'launch.howItWorks.step1.desc': 'Take a photo and describe the issue — it takes 30 seconds',
    'launch.howItWorks.step2': 'Track',
    'launch.howItWorks.step2.desc': 'Follow your report in real time and mobilize the community',
    'launch.howItWorks.step3': 'Resolve',
    'launch.howItWorks.step3.desc': 'Local authorities act and you see the impact on your city',

    // Launch Stats
    'launch.stats.categories': 'Report categories',
    'launch.stats.resolution': 'Resolution rate',
    'launch.stats.time': 'To create a report',
    'launch.stats.roi': 'Social ROI',

    // Launch Social Proof
    'launch.social.title': 'What our early testers say',
    'launch.social.quote1': 'Finally an app that gives power back to citizens!',
    'launch.social.author1': 'Marie L.',
    'launch.social.role1': 'Resident, Lyon',
    'launch.social.quote2': 'The interface is intuitive — reporting takes 30 seconds.',
    'launch.social.author2': 'Thomas R.',
    'launch.social.role2': 'Beta tester',
    'launch.social.quote3': 'An essential tool for local authorities.',
    'launch.social.author3': 'Sophie M.',
    'launch.social.role3': 'City council member',

    // Launch Final CTA
    'launch.finalCta.title': 'Ready to improve your city?',
    'launch.finalCta.subtitle': 'Join the first engaged citizens and get priority access.',
    'launch.finalCta.subscriberCount': '{{count}}+ people already signed up',
    'launch.finalCta.beFirst': 'Be among the first',
    'launch.finalCta.launchSoon': 'Launching soon',

    // Welcome (Product Hunt + BetaList)
    'welcome.badge': 'Thanks Product Hunt! 🎉',
    'welcome.badge.betalist': 'Discovered on BetaList 🚀',
    'welcome.title': 'Your city deserves',
    'welcome.titleAccent': 'better.',
    'welcome.subtitle': 'Potholes, broken lights, illegal dumping — report in 30 seconds, your city acts. Join the citizen movement.',
    'welcome.subtitle.betalist': 'You\'re among the first to discover KLIKAN. Report urban issues in 30 seconds and transform your city. Exclusive early adopter access.',
    'welcome.highlight1.title': '30s to report',
    'welcome.highlight1.desc': 'Photo + automatic geolocation',
    'welcome.highlight2.title': '76% resolution rate',
    'welcome.highlight2.desc': 'Transparent real-time tracking',
    'welcome.highlight3.title': '2,847 active citizens',
    'welcome.highlight3.desc': 'Engaged and growing community',
    'welcome.earlyAccess': '🚀 Priority access — Product Hunt offer',
    'welcome.earlyAccess.betalist': '🚀 Exclusive access — BetaList offer',
    'welcome.guarantee': '✓ Free forever · ✓ Zero spam · ✓ Priority access',
    'welcome.guarantee.betalist': '✓ Free forever · ✓ Zero spam · ✓ Early adopter badge',
    'welcome.betalist.featured': 'Selected and featured on BetaList',
    'welcome.betalist.spotsLeft': 'Only {{count}} early adopter spots left',
    'welcome.betalist.launchIn': 'Launch in',
    'welcome.betalist.days': 'd',
    'welcome.betalist.hours': 'h',
    'welcome.betalist.minutes': 'min',
    'welcome.betalist.seconds': 's',
    'welcome.explore': 'Explore the platform',
    'welcome.seeImpact': 'See our impact',
    'welcome.howItWorks': 'How it works',
    'welcome.step1': 'Photograph',
    'welcome.step1.desc': 'Take a photo of the issue',
    'welcome.step2': 'Report',
    'welcome.step2.desc': 'Automatic geolocation + category',
    'welcome.step3': 'Track',
    'welcome.step3.desc': 'Notification when it\'s resolved',

    // Welcome Social Proof
    'welcome.social.title': 'They already trust us',
    'welcome.social.subscriberCount': '{{count}} citizens signed up for early access',
    'welcome.social.quote1': 'Finally a platform that gives citizens a real voice. I reported a pothole and it was fixed in 5 days!',
    'welcome.social.author1': 'Marie D.',
    'welcome.social.role1': 'Engaged citizen, Lyon',
    'welcome.social.quote2': 'An essential tool for our municipality. Transparency with residents has completely changed.',
    'welcome.social.author2': 'Thomas R.',
    'welcome.social.role2': 'Municipal councilor, Bordeaux',
    'welcome.social.quote3': 'Simple, effective and free. Our association uses it daily to coordinate our actions.',
    'welcome.social.author3': 'Sophie L.',
    'welcome.social.role3': 'Association president, Nantes',

    // Categories
    'cat.voirie': 'Roads',
    'cat.voirie.desc': 'Issues related to roads, sidewalks and road infrastructure',
    'cat.proprete': 'Cleanliness',
    'cat.proprete.desc': 'Illegal dumping, overflowing bins, graffiti',
    'cat.eclairage': 'Lighting',
    'cat.eclairage.desc': 'Faulty streetlights or poorly lit areas',
    'cat.transport': 'Transport',
    'cat.transport.desc': 'Issues related to public transport or mobility',
    'cat.espaces-verts': 'Green spaces',
    'cat.espaces-verts.desc': 'Maintenance of parks, gardens and plantings',
    'cat.eau': 'Water',
    'cat.eau.desc': 'Leaks, floods, drainage problems',
    'cat.securite': 'Safety',
    'cat.securite.desc': 'Dangerous areas or issues requiring authority intervention',
    'cat.pollution-air': 'Air pollution',
    'cat.pollution-air.desc': 'Issues related to air quality and pollutant emissions',
    'cat.pollution-eau': 'Water pollution',
    'cat.pollution-eau.desc': 'Contamination of rivers, ponds or other water bodies',
    'cat.nuisance-sonore': 'Noise pollution',
    'cat.nuisance-sonore.desc': 'Excessive noise, acoustic issues in public spaces',
    'cat.biodiversite': 'Biodiversity',
    'cat.biodiversite.desc': 'Issues related to species and ecosystem protection',
    'cat.flore-invasive': 'Invasive flora',
    'cat.flore-invasive.desc': 'Presence of invasive plants requiring treatment',
    'cat.inondation': 'Flooding',
    'cat.inondation.desc': 'Flooded areas or flood-prone zones',
    'cat.ilot-chaleur': 'Heat island',
    'cat.ilot-chaleur.desc': 'Particularly hot urban areas requiring improvements',
    'cat.erosion': 'Erosion',
    'cat.erosion.desc': 'Soil or bank erosion problems',
    'cat.ventilation': 'Ventilation',
    'cat.ventilation.desc': 'Air circulation issues in public spaces',
    'cat.pollution-visuelle': 'Visual pollution',
    'cat.pollution-visuelle.desc': 'Visual nuisances, wild advertising, aesthetic degradation',
    'cat.commerce-alimentaire': 'Food retail',
    'cat.commerce-alimentaire.desc': 'Issues related to food businesses',
    'cat.commerce': 'Retail',
    'cat.commerce.desc': 'Issues related to local shops and services',
    'cat.urgence': 'Emergency',
    'cat.urgence.desc': 'Situations requiring rapid intervention',
    'cat.terrain-abandonne': 'Abandoned land',
    'cat.terrain-abandonne.desc': 'Wasteland or abandoned plots causing issues',

    // Mobile bottom nav
    'nav.home': 'Home',
    'nav.stats': 'Stats',
    'nav.report': 'Report',
    'nav.favorites': 'Favorites',
    'nav.profile': 'Profile',

    // Toasts
    'toast.alreadySubscribed': 'You\'re already signed up! 🎉',
    'toast.welcomeEarly': 'Welcome! Priority access guaranteed 🎉',
    'toast.welcomePH': 'Welcome! Priority access guaranteed 🎉',
    'toast.welcomeBL': 'Welcome early adopter! Exclusive access guaranteed 🚀',
    'toast.error': 'An error occurred. Please try again.',
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useLocalStorage<Language>('user_language', 'fr');

  const translate = (key: string, params?: Record<string, string | number>): string => {
    let text = translations[language][key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{{${k}}}`, String(v));
      });
    }
    return text;
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    document.documentElement.lang = lang;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translate }}>
      {children}
    </LanguageContext.Provider>
  );
};
